import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

const CONFIRM = "ELIMINA";
const chip = (on) => `px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-red-600 text-white border-red-600" : "bg-white border-stone-200 text-stone-600"}`;

export function BulkDeleteDialog({ open, onClose, mansioni, initialSince, initialUntil }) {
  const qc = useQueryClient();
  const [since, setSince] = useState(initialSince);
  const [until, setUntil] = useState(initialUntil);
  const [ids, setIds] = useState([]);
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { if (open) { setSince(initialSince); setUntil(initialUntil); setIds([]); setStep(1); setPreview(null); setText(""); } }, [open, initialSince, initialUntil]);

  const body = { since, until, mansione_ids: ids };
  const previewMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/turni/bulk-delete/preview", body)).data,
    onSuccess: (d) => { setPreview(d); setStep(2); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/turni/bulk-delete", { ...body, confirm_text: text, expected_count: preview.count })).data,
    onSuccess: (d) => { toast.success(`${d.deleted} turni eliminati`); qc.invalidateQueries({ queryKey: ["turni"] }); qc.invalidateQueries({ queryKey: ["planner-calendar"] }); onClose(); },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setStep(1); },
  });
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-delete-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle size={18}/> Elimina turni in blocco</DialogTitle></DialogHeader>
        <div className="text-[11px] uppercase tracking-widest text-stone-500">Verifica {step} di 2</div>
        {step === 1 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dal</Label><Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1.5" data-testid="bulk-since"/></div>
              <div><Label>Al</Label><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1.5" data-testid="bulk-until"/></div>
            </div>
            <div>
              <Label>Mansioni (nessuna selezionata = tutte le mansioni interne)</Label>
              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                {mansioni.map((m) => <button key={m.id} type="button" className={chip(ids.includes(m.id))} onClick={() => toggle(m.id)} data-testid={`bulk-mansione-${m.id}`}>{m.name}</button>)}
              </div>
            </div>
            <p className="text-xs text-stone-500">Gli impegni esterni non vengono toccati.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bulk-step2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              Stai per eliminare <b data-testid="bulk-count">{preview.count}</b> turni dal {since.split("-").reverse().join("/")} al {until.split("-").reverse().join("/")}. L'operazione non si può annullare.
            </div>
            {preview.by_mansione.length > 0 && (
              <div className="max-h-40 overflow-y-auto text-sm divide-y divide-stone-100 border border-stone-200 rounded-lg">
                {preview.by_mansione.map((m) => <div key={m.mansione_id} className="flex justify-between px-3 py-1.5"><span>{m.name}</span><b>{m.count}</b></div>)}
              </div>
            )}
            <div><Label>Per confermare scrivi <b>{CONFIRM}</b></Label><Input value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5" autoFocus data-testid="bulk-confirm-text"/></div>
          </div>
        )}
        <DialogFooter>
          {step === 2 ? <Button variant="outline" onClick={() => setStep(1)} data-testid="bulk-back">Indietro</Button> : <Button variant="outline" onClick={onClose}>Annulla</Button>}
          {step === 1 ? (
            <Button className="bg-red-600 hover:bg-red-700" disabled={!since || !until || previewMut.isPending} onClick={() => previewMut.mutate()} data-testid="bulk-next">Continua</Button>
          ) : (
            <Button className="bg-red-600 hover:bg-red-700" disabled={!preview.count || text.trim().toUpperCase() !== CONFIRM || delMut.isPending} onClick={() => delMut.mutate()} data-testid="bulk-confirm">
              {delMut.isPending ? "Elimino…" : `Elimina ${preview.count} turni`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
